/**
 * Hiring-demand board — adding a role.
 *
 * The composer's month targets live in the SAME row as its inputs. They used to
 * render in the row underneath, which meant "now pick a start month →" pointed
 * at cells that were not clickable — the one interaction in the whole board
 * nobody could complete.
 *
 * Split out of DemandRows.tsx when the role row grew its move-to-team controls:
 * the two together were over the 500-line ceiling, and adding a role and moving
 * one are separate jobs anyway.
 */
import { useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import type { HireReason } from '../../../types/taDemand';
import type { Track } from './demandModel';
import { ACTIONS, CELL, indentPx, NameCells } from './demandGrid';
import { parseNum, zoneClass } from './demandUi';
import { LEVELS } from './demandLevels';

const REASONS: { value: HireReason; label: string }[] = [
  { value: 'new', label: 'New hire' },
  { value: 'backfill', label: 'Backfill' },
];

export function ComposerRow({
  tracks,
  depth,
  saving,
  onPlace,
  onCancel,
  error,
}: {
  tracks: Track[];
  depth: number;
  saving: boolean;
  onPlace: (
    month: string,
    fields: { title: string; level: string; reason: HireReason; count: number; fte: number },
  ) => void;
  onCancel: () => void;
  /**
   * Why the last attempt did not save. Rendered INSIDE this row: a banner at the
   * top of a scrolled board is invisible, which is how a failed save reads as
   * "clicking the month did nothing".
   */
  error?: string | null;
}) {
  const [title, setTitle] = useState('');
  const [level, setLevel] = useState('IC2');
  const [reason, setReason] = useState<HireReason>('new');
  const [count, setCount] = useState(1);
  const [fte, setFte] = useState(1);

  return (
    <div
      className="grid min-h-[42px] items-stretch border-b border-gray-100 bg-blue-50"
      style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
    >
      <NameCells
        depth={depth}
        bg="bg-blue-50"
        className="shadow-[inset_2px_0_0_0_theme(colors.blue.500)]"
        who={
          <input
            autoFocus
            className="h-6 min-w-0 flex-1 rounded-sm border border-gray-300 bg-white px-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
            placeholder="Role title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onCancel();
            }}
          />
        }
      >
        {/* 2 · level */}
        <div className={CELL}>
          <select
            className="h-6 w-full rounded-sm border border-gray-300 bg-white px-0.5 text-xs"
            value={level}
            aria-label="Level"
            onChange={(e) => setLevel(e.target.value)}
          >
            {LEVELS.map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
        </div>
        {/* 3 · FTE */}
        <div className={`${CELL} tabular-nums`}>
          <input
            type="number"
            className="h-6 w-8 rounded-sm border border-gray-300 bg-white text-center text-xs"
            value={count}
            min={1}
            max={20}
            step={1}
            aria-label="Number of hires"
            onChange={(e) => setCount(Math.min(20, Math.max(1, Math.round(parseNum(e.target.value) ?? 1))))}
          />
          <span className="shrink-0 text-[11px] text-gray-400">×</span>
          <input
            type="number"
            className="h-6 w-11 rounded-sm border border-gray-300 bg-white text-center text-xs"
            value={fte}
            min={0.05}
            max={1}
            step={0.05}
            aria-label="FTE per hire"
            onChange={(e) => setFte(Math.min(1, Math.max(0.05, parseNum(e.target.value) ?? 1)))}
          />
        </div>
        {/* 4 · who */}
        <div className={CELL}>
          <select
            className="h-6 w-full rounded-sm border border-gray-300 bg-white px-0.5 text-xs"
            value={reason}
            aria-label="Reason for hiring"
            onChange={(e) => setReason(e.target.value as HireReason)}
          >
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        {/* 5 · standing */}
        <div className={CELL}>
          <span className="shrink-0 rounded-sm border border-dashed border-blue-500 px-1.5 text-[11px] font-semibold leading-4 text-blue-700">
            draft
          </span>
        </div>
        {/* 6 · actions */}
        <div className={ACTIONS}>
          {/* The failure is reported ON THIS ROW: a banner at the top of a
              scrolled board is invisible, which is how a failed save reads as
              "clicking the month did nothing". */}
          {error && (
            <span
              role="alert"
              className="min-w-0 truncate rounded-sm bg-rose-50 px-1.5 text-[11px] font-semibold leading-4 text-rose-700"
              title={error}
            >
              {error}
            </span>
          )}
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-sm text-gray-500 hover:bg-white hover:text-rose-600"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </NameCells>

      {tracks.map((t, i) => {
        /*
          Three reasons a month cannot be clicked, and they are NOT the same
          reason. `saving && !disabled` was never true — `disabled` already
          contained `saving` — so the spinner never rendered and an in-flight
          save looked exactly like a dead button, which is the "clicking the
          month did nothing" reading this file exists to have fixed. The tooltip
          had the same root cause: it blamed a past month for a collapsed group
          and for a save in progress.
        */
        const closed = t.kind === 'group' || t.month.past;
        const disabled = closed || saving;
        return (
          <div key={i} className={`flex items-center justify-center ${zoneClass(t)}`}>
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                t.kind === 'month' && onPlace(t.month.key, { title, level, reason, count, fte })
              }
              title={
                t.kind === 'group'
                  ? 'Expand the past months to start a role in one'
                  : t.month.past
                    ? 'A new role cannot start in a past month'
                    : saving
                      ? 'Saving…'
                      : `Start ${t.month.label} ${t.month.year}`
              }
              className={`grid h-[26px] w-[26px] place-items-center rounded-full border-2 text-sm font-bold ${
                disabled
                  ? 'cursor-not-allowed border-gray-200 text-gray-300'
                  : 'border-dashed border-blue-500 bg-white text-blue-600 hover:border-solid hover:bg-blue-600 hover:text-white'
              }`}
            >
              {saving && !closed ? <Loader2 className="h-3 w-3 animate-spin" /> : '+'}
            </button>
          </div>
        );
      })}

      {/* Count and FTE live in column 3 now, where they line up with the
          same values on every other row. */}
    </div>
  );
}

export function AddRoleRow({
  tracks,
  depth,
  label,
  onClick,
}: {
  tracks: Track[];
  depth: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <div
      className="grid min-h-[34px] items-stretch border-b border-gray-100"
      style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
    >
      <div
        className="sticky left-0 z-[2] flex min-w-0 items-center border-r border-gray-200 bg-white pr-2"
        style={{ paddingLeft: indentPx(depth) }}
      >
        <button
          type="button"
          onClick={onClick}
          className="inline-flex items-center gap-1.5 rounded-sm px-1.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50"
        >
          <Plus className="h-3 w-3" />
          {label}
        </button>
      </div>
      {tracks.map((t, i) => (
        <div key={i} className={zoneClass(t)} />
      ))}
    </div>
  );
}
